import {faCamera} from "@fortawesome/pro-solid-svg-icons/faCamera";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useClientAccountOverview} from "@twigsby/client-data";

import {
    AvatarSize,
    Button,
    ColourOutline,
    ColourSolid,
    ContentBlock,
    ContentLoader,
    FlexGap,
    FlexJustify,
    Pill,
    StackHorizontal,
    StackVertical,
    useLayout,
    useTranslation
} from "@twigsby/frontend-framework";
import {useWindowScrollPositions} from "@twigsby/frontend-framework/lib/Hooks/useWindowScrollPosition";
import {FormatCurrency} from "@twigsby/helpers";
import React from "react";
import {useUser} from "../../Providers/UserProvider";
import {EndpointError} from "../EndpointError";
import {UserAvatar} from "../User/UserAvatar";
import {UserMenuDropdownMenu} from "../UserMenu";

type AccountHeaderType = {
    inOverview?: boolean;
}

const AccountHeader = (props: AccountHeaderType) => {
    const { t } = useTranslation();
    const windowPosition = useWindowScrollPositions();
    const { SelectNewAvatar } = useUser();
    const { isTiny, isLarge} = useLayout();
    const { data, status, error } = useClientAccountOverview();
    const { data: userData, status: userStatus, error: userError } = useClientAccountOverview();

    return (
        <>
            {status === 'error' && (
                <EndpointError {...error} />
            )}

            {status === 'loading' && (
                <StackHorizontal>
                    <ContentLoader rounded={true} childCount={1} childWidth={isLarge ? '6em' : '4.5em'} childHeight={isLarge ? '6em' : '4.5em'} />

                    <div className={'flex-column justify-center ml-sm mr-a flex-gap-xs'}>
                        <ContentLoader childHeight={'2.4em'} childCount={1} childWidth={'12em'} />
                        <ContentLoader childHeight={'1.5em'} childCount={1} childWidth={'19em'} />
                    </div>
                </StackHorizontal>
            )}

            {status !== 'loading' && status !== 'error' && userStatus !== 'error' && (
                <div className={`user-account-header /*${windowPosition.scrollY !== 0 ? 'scrolling' : ''}*/`}>
                    <div className={'user-avatar-wrapper'} onClick={SelectNewAvatar}>
                        <div className={'user-avatar-icon'}>
                            <FontAwesomeIcon icon={faCamera} size={"2x"} />
                        </div>
                        <UserAvatar size={isLarge ? AvatarSize.ExtraLarge : AvatarSize.Large} />
                    </div>

                    <StackVertical gap={FlexGap.ExtraSmall} justify={FlexJustify.Center} extraClass={'ml-sm mr-a'}>
                        <h4 className={'m-0'}>{data?.name}</h4>

                        <StackHorizontal gap={FlexGap.ExtraSmall} wrap={true}>
                            {!data?.isVerified && (
                                <Pill colour={ColourSolid.Alert} content={t('labels.Unverified')} />
                            )}
                            <Pill content={`${t('labels.clientIdNo')}: ${data?.clientIdNo}`} />
                            <Pill content={`${t('labels.plan')}: ${data?.planName}`} />
                            {data?.isBusiness && (
                                <Pill content={t('labels.businessAccount')} />
                            )}
                        </StackHorizontal>
                    </StackVertical>

                    <div className={'flex-b flex-gap-sm flex-center ml-a '}>
                        {isLarge && (
                            <Button
                                title={t('actions.topUp')}
                                colour={data?.balance <= 0 ? ColourSolid.Alert : ColourSolid.Secondary}
                                actionLink={{ url: '/manage-account/payment/top-up', title: `${t('actions.topUp')} • ${FormatCurrency(data?.balance)}`}}
                            />
                        )}

                        <UserMenuDropdownMenu
                            customTrigger={
                                <Button
                                    title={t('actions.manageAccount')}
                                    showArrow={true}
                                />
                            }
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export {AccountHeader}
